import { useMemo } from 'react';

import { SENTRY_DSN } from '@chk/env-variables';
import {
  ERROR_TEST_ID,
  getComponentByStatus,
  useTranslationsContext,
} from '@state/checkout-contexts';
import { ChkGenericError } from '@ui/checkout-components';
import {
  APP_INTEGRATION_TYPE,
  getPaymentRequestId,
  initErrorTracking,
  IS_PRODUCTION,
  setSentryTags,
} from '@utility/checkout-utils';
import { usePageTitle } from '@utility/checkout-utils';
import {
  COMMON_EVENTS,
  MixpanelIntegration,
  NewRelicIntegration,
} from '@utility/data-layer';

import { PageLoadingPlaceholder } from '../components/PageLoading/PageLoadingPlaceholder';
import { NEW_RELIC_CONFIG } from '../global.constants';
import { useJwtContext } from '../hooks/useJwtContext/useJwtContext';
import { useScaSubject } from '../providers/ScaSubjectProvider/ScaSubjectProvider.hooks';
import { dataLayer } from '../utils/dataLayer';
import { getViewKey } from '../utils/index.utils';
import { TRANSLATION_KEYS } from '../utils/TranslationKeys';

import { FIELDS_TO_TITLE_MAP, FIELDS_TO_VIEW_MAP } from './app.constants';
import {
  useRedirectOutsideSca,
  useTrackGlobalDataLayerProperties,
  useWarnAboutMissingToken,
} from './app.hooks';

initErrorTracking({ dsn: SENTRY_DSN });
const paymentRequestId = getPaymentRequestId();

dataLayer
  .addIntegration(
    MixpanelIntegration.getInstance({
      record_sessions_percent: 5,
      record_mask_text_selector: '',
      record_block_selector: '',
    })
  )
  .addIntegration(
    NewRelicIntegration.getInstance(NEW_RELIC_CONFIG, IS_PRODUCTION)
  )
  .init({
    application: 'sca-app',
    integrationType: APP_INTEGRATION_TYPE.HOSTED,
    paymentId: paymentRequestId,
  });

if (paymentRequestId) {
  dataLayer.trackIdentification(paymentRequestId);

  setSentryTags({ voltId: paymentRequestId });
}

dataLayer.trackEvent({
  name: COMMON_EVENTS.APP_IS_LOADING,
});

export function App() {
  const { t } = useTranslationsContext();
  const { payload } = useJwtContext();

  const {
    subject,
    isLoading: isSubjectLoading,
    isLoadingError: isSubjectLoadingError,
  } = useScaSubject();

  const viewKey = useMemo(() => getViewKey(subject), [subject]);

  useTrackGlobalDataLayerProperties();
  useRedirectOutsideSca();
  useWarnAboutMissingToken();
  usePageTitle({
    mainTitle: t(TRANSLATION_KEYS.VOLT_SCA),
    suffix: viewKey ? FIELDS_TO_TITLE_MAP[viewKey] : null,
  });

  return useMemo(() => {
    const isReady =
      !payload || isSubjectLoading || isSubjectLoadingError || Boolean(subject);

    return getComponentByStatus({
      status: viewKey,
      componentsMap: FIELDS_TO_VIEW_MAP,
      isLoading: isSubjectLoading,
      isReady: isReady,
      loadingComponent: <PageLoadingPlaceholder />,
      errorComponent: (
        <ChkGenericError
          data-testid={ERROR_TEST_ID}
          title={t(TRANSLATION_KEYS.SOMETHING_WENT_WRONG)}
          text={t(TRANSLATION_KEYS.OOPS_IT_APPEARS_THERE_IS_AN_ISSUE)}
        />
      ),
    });
  }, [payload, isSubjectLoading, isSubjectLoadingError, subject, viewKey, t]);
}

export default App;
